/**
 * My settings (the owner, 2026-10-29; ADR 0200): each user's own, owners too,
 * from the user menu. How Axon Time knows their work, which they set up
 * themselves: their time zone, SkySwitch extension, ScreenConnect name and
 * work computers, Datto RMM user, mail, office and VPN addresses. Their
 * Autotask resource is shown, read only: an owner sets it.
 *
 * The sections are Mappings → Techs' own (TechPanel.tsx), on this user's row
 * from `GET /api/v1/techs/{id}`, which lists only the choices no one else
 * holds. Each saves at once and refreshes this page, as it does the owner's.
 * Nothing about anyone else is here: that stays on the owner's tabs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useMemo, useState } from "react";

import { ApiError, retryUnlessRefused } from "../../api/client";
import type { Tech, TechMapping, UserChanges } from "../../api/types";
import { ME_QUERY_KEY, useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Panel } from "../../components/ui/Panel";
import { TECH_MAPPING_KEY } from "../mappings/keys";
import {
  DattoSection,
  MailSection,
  OfficeAddressesSection,
  ScreenConnectSection,
  SkySwitchSection,
  VpnAddressesSection,
} from "../mappings/TechPanel";
import { userName } from "../mappings/techStatus";
import { TimeZoneSelect } from "./TimeZoneSelect";
import { zonesWith } from "./zones";

/** Under the mapping's key, so a section's save refreshes it as it does Mappings → Techs. */
function ownKey(userId: string) {
  return [...TECH_MAPPING_KEY, "own", userId];
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Panel aria-labelledby={id}>
      <h2 id={id} className="text-lg font-semibold tracking-tight">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </Panel>
  );
}

function TimeZone() {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const zones = useMemo(() => zonesWith([me.timezone]), [me.timezone]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (changes: UserChanges) => api.updateUser(me.id, changes),
    onSuccess: () => {
      setError(undefined);
      setSaved(true);
      // Every day and time the app shows is in it.
      return queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
    onError: (e) => {
      const named = e instanceof ApiError ? e.fieldErrors.find((f) => f.field === "timezone") : undefined;
      setError(named?.message ?? (e instanceof Error ? e.message : "Something went wrong."));
    },
  });
  const { send, saving } = useOneSaveAtATime(save);
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted">
        Your day starts and ends at midnight here, and every time Axon Time shows you is in it. A change applies from
        now on: days already built keep theirs until they&apos;re rebuilt, the last 7 each night.
      </p>
      <TimeZoneSelect
        person={me.display_name ?? me.email ?? "you"}
        zones={zones}
        shown={saving?.value.timezone ?? me.timezone}
        status={saving !== null ? "Saving…" : saved ? "Saved." : undefined}
        error={error}
        onChoose={(zone) => {
          setSaved(false);
          send({ timezone: zone });
        }}
      />
    </div>
  );
}

/** Their Autotask resource, read only: time is written to Autotask as it, so an owner sets it. */
function AutotaskResource({ tech, mapping }: { tech: Tech; mapping: TechMapping }) {
  const resource = mapping.resources.find((r) => r.autotask_resource_id === tech.autotask_resource_id);
  return (
    <section aria-label="Autotask resource" className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold">Autotask resource</h3>
      {resource ? (
        <p>
          {resource.name}
          {resource.default_role ? (
            <span className="text-muted">{` · ${resource.default_role.name}`}</span>
          ) : null}
        </p>
      ) : (
        <p className="text-muted">Not mapped yet.</p>
      )}
      <p className="text-sm text-muted">Your time is logged to Autotask as this resource, so an owner sets it.</p>
    </section>
  );
}

function HowWorkIsKnown({ tech, mapping }: { tech: Tech; mapping: TechMapping }) {
  const nameId = useId();
  return (
    <>
      {/* What each field is described by, as the owner's panel has the tech's name in the row above. */}
      <span id={nameId} className="sr-only">
        {userName(tech)}
      </span>
      <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        <AutotaskResource tech={tech} mapping={mapping} />
        <SkySwitchSection tech={tech} mapping={mapping} labelledBy={nameId} self />
        <ScreenConnectSection tech={tech} mapping={mapping} labelledBy={nameId} />
        <DattoSection tech={tech} mapping={mapping} labelledBy={nameId} self />
        <MailSection tech={tech} labelledBy={nameId} self />
        <OfficeAddressesSection tech={tech} labelledBy={nameId} self />
        <VpnAddressesSection tech={tech} mapping={mapping} labelledBy={nameId} self />
      </div>
    </>
  );
}

export function MySettingsPage() {
  const { api, me } = useSession();
  const own = useQuery({
    queryKey: ownKey(me.id),
    queryFn: () => api.getOwnSettings(me.id),
    retry: retryUnlessRefused,
  });
  const tech = own.data?.techs[0];

  return (
    <>
      <Panel>
        <h1 className="text-xl font-semibold tracking-tight">My settings</h1>
        <p className="mt-1 text-muted">
          How Axon Time knows your work: set up your own, and keep it right when something changes. Each change saves
          as you make it, and an owner can see it.
        </p>
      </Panel>
      <Section id="my-time-zone" title="Time zone">
        <TimeZone />
      </Section>
      <Section id="my-work" title="How your work is known">
        {own.error && !own.data ? (
          <p role="alert" className="text-muted">
            Your settings could not be loaded. Reload the page to try again.
          </p>
        ) : own.data && tech ? (
          <HowWorkIsKnown tech={tech} mapping={own.data} />
        ) : (
          <p role="status" className="text-muted">
            Loading…
          </p>
        )}
      </Section>
    </>
  );
}
